import React from 'react';

import {
  faCalendar,
  faEye,
  faMessage,
  faPhone,
  faUser
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  Card,
  Grid,
  Group,
  Stack,
  Text,
  Tooltip
} from '@mantine/core';
import { Link } from 'react-router-dom';

import { type Encounter } from './types';
import { FormatPhone } from '../../utils/format';
import { getIntentColor, getWhoColor } from '../../utils/theme';

interface EncounterCardProps {
  encounter: Encounter;
  showClient?: boolean;
  showCaller?: boolean;
  badgeSize?: 'sm' | 'md';
  onViewDetails: (encounter: Encounter) => void;
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const EncounterCard: React.FC<EncounterCardProps> = ({
  encounter,
  showClient = false,
  showCaller = true,
  badgeSize = 'sm',
  onViewDetails
}) => {
  return (
    <Card withBorder p="md" shadow="sm">
      <Grid>
        <Grid.Col span={{ base: 12, md: 8, lg: 9 }}>
          <Stack gap="xs">
            <Group gap="sm">
              {showClient && (
                <>
                  <FontAwesomeIcon icon={faUser} size="sm" />
                  <Text
                    c="blue"
                    component={Link}
                    fw={500}
                    size="lg"
                    style={{
                      textDecoration: 'none',
                      color: 'inherit'
                    }}
                    to={`/client/${encounter._client.id}/encounters`}
                  >
                    {encounter._client.firstName} {encounter._client.lastName}
                  </Text>
                </>
              )}
              <Badge
                color={getWhoColor(encounter.who)}
                size={badgeSize}
                variant="filled"
              >
                {encounter.who}
              </Badge>
              <Badge
                color={getIntentColor(encounter.intent)}
                size={badgeSize}
                variant="filled"
              >
                {encounter.intent}
              </Badge>
            </Group>

            <Group gap="md">
              {showCaller && (
                <Group gap="xs">
                  <FontAwesomeIcon icon={faPhone} size="sm" />
                  {encounter._caller ? (
                    <Text
                      c="blue"
                      component={Link}
                      size="sm"
                      style={{
                        textDecoration: 'none',
                        color: 'inherit'
                      }}
                      to={`/caller/${encounter._caller.id}/encounters`}
                    >
                      {encounter._caller.firstName || encounter._caller.lastName
                        ? `${encounter._caller.firstName || ''} ${encounter._caller.lastName || ''}`.trim()
                        : FormatPhone(encounter._caller.phone)}
                    </Text>
                  ) : (
                    <Text c="dimmed" size="sm">
                      {FormatPhone(encounter.externalPhone)}
                    </Text>
                  )}
                </Group>
              )}
              <Group gap="xs">
                <FontAwesomeIcon icon={faCalendar} size="sm" />
                <Text c="dimmed" size="sm">
                  {formatDate(encounter.createdAt)}
                </Text>
              </Group>
              {encounter.transcripts && encounter.transcripts.length > 0 && (
                <Group gap="xs">
                  <FontAwesomeIcon icon={faMessage} size="sm" />
                  <Text c="dimmed" size="sm">
                    {encounter.transcripts.length} messages
                  </Text>
                </Group>
              )}
            </Group>

            {encounter.insights && (
              <Text lineClamp={2} size="md">
                {encounter.insights}
              </Text>
            )}
          </Stack>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4, lg: 3 }}>
          <Group gap="xs" justify="flex-end">
            <Tooltip label="View Details">
              <Button
                color="blue"
                leftSection={<FontAwesomeIcon icon={faEye} size="sm" />}
                size="sm"
                variant="light"
                onClick={() => onViewDetails(encounter)}
              >
                View Details
              </Button>
            </Tooltip>
          </Group>
        </Grid.Col>
      </Grid>
    </Card>
  );
};

export default EncounterCard;
